import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Tool of the Week" banner — fully automated weekly rotation across all
 * active affiliate partners. The backend deterministically picks one
 * partner per ISO week (rolls over Monday 00:00 UTC), so visitors see a
 * fresh featured tool every week without any admin action.
 *
 * Banner copy uses (in priority order):
 *   1. partner.weekly_spotlight_pitch  — optional admin override
 *   2. partner.customer_benefit        — e.g. "10% off for life"
 *   3. partner.tagline                 — fallback
 *
 * Click routes through the tracked redirect, so attribution stays clean.
 * Renders nothing while loading or if the pool is empty — degrades silently.
 */
export default function WeeklySpotlightBanner() {
  const [partner, setPartner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/affiliate-partners/spotlight`);
        if (!cancelled) setPartner(res.data?.partner || null);
      } catch {
        // Silent — non-critical UI.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!partner) return null;

  const pitch =
    partner.weekly_spotlight_pitch ||
    partner.customer_benefit ||
    partner.tagline ||
    `Try ${partner.name} this week`;

  return (
    <a
      href={`/featured/${partner.slug}`}
      className="group block relative overflow-hidden rounded-2xl border border-[#00F0FF]/30 bg-gradient-to-r from-[#00F0FF]/[0.08] via-black/40 to-[#00FF94]/[0.06] hover:border-[#00F0FF]/60 transition-colors"
      data-testid="weekly-spotlight-banner"
    >
      {/* Subtle scanline accent — pure brand cyan, no purple */}
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/60 to-transparent" />

      <div className="relative flex items-center gap-3 sm:gap-6 p-3 sm:p-5 md:p-6 min-w-0">
        {/* Logo tile */}
        <div className="shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-black/70 border border-white/10 flex items-center justify-center overflow-hidden p-2">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <Sparkles className="w-6 h-6 text-[#00F0FF]" />
          )}
        </div>

        {/* Copy */}
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[10px] font-bold uppercase tracking-wider text-[#00F0FF] whitespace-nowrap"
              data-testid="spotlight-pill"
            >
              <Sparkles className="w-3 h-3" /> Tool of the Week
            </span>
          </div>
          <p className="text-sm sm:text-lg font-bold text-white truncate">
            <span className="text-[#00F0FF]">{partner.name}</span>
            <span className="mx-2 text-neutral-600">·</span>
            <span className="text-white/90 font-semibold">{pitch}</span>
          </p>
          {partner.tagline && pitch !== partner.tagline && (
            <p className="mt-0.5 text-xs sm:text-sm text-neutral-400 truncate">
              {partner.tagline}
            </p>
          )}
        </div>

        {/* CTA */}
        <div
          className="shrink-0 hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#00F0FF] group-hover:translate-x-1 transition-transform"
          data-testid="spotlight-cta"
        >
          See why <ArrowRight className="w-4 h-4" />
        </div>
        <ArrowRight className="sm:hidden shrink-0 w-5 h-5 text-[#00F0FF] group-hover:translate-x-0.5 transition-transform" />
      </div>
    </a>
  );
}
